import React, { useState, useRef } from "react";
import noimg from "../../Images/noimg.jpg";

/* ---------- inline icons ---------- */
const IconChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconCamera = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

/**
 * ManualImageSlider
 * Props:
 *   images        – string[] of image URLs
 *   alt           – base alt text
 *   variant       – "hero" | "room"  (drives CSS sizing)
 *   onImageClick  – (index) => void  (called on tap of the slide)
 */
const ManualImageSlider = ({
  images = [],
  alt = "image",
  variant = "hero",
  onImageClick,
}) => {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const total = list.length;
  const [index, setIndex] = useState(0);

  const startX = useRef(null);
  const endX = useRef(null);

  const goTo = (i) => {
    if (!total) return;
    if (i < 0) setIndex(total - 1);
    else if (i >= total) setIndex(0);
    else setIndex(i);
  };

  const onTouchStart = (e) => {
    endX.current = null;
    startX.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e) => {
    endX.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (startX.current == null || endX.current == null) return;
    const d = startX.current - endX.current;
    if (d > 45) goTo(index + 1);
    else if (d < -45) goTo(index - 1);
    startX.current = null;
    endX.current = null;
  };

  if (!total) {
    return (
      <div className={`rc-slider rc-slider--${variant}`}>
        <div className="rc-slide">
          <img src={noimg} alt="no image" className="rc-img" draggable={false} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rc-slider rc-slider--${variant}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="rc-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {list.map((src, i) => (
          <div
            className="rc-slide"
            key={i}
            onClick={() => onImageClick && onImageClick(i)}
          >
            <img
              src={src}
              alt={`${alt} ${i + 1}`}
              className="rc-img"
              draggable={false}
              loading={i === 0 ? "eager" : "lazy"}
              onError={(e) => { e.currentTarget.src = noimg; }}
            />
          </div>
        ))}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            className="rc-nav rc-nav-prev"
            onClick={(e) => { e.stopPropagation(); goTo(index - 1); }}
            aria-label="Previous image"
          >
            <IconChevronLeft />
          </button>

          <button
            type="button"
            className="rc-nav rc-nav-next"
            onClick={(e) => { e.stopPropagation(); goTo(index + 1); }}
            aria-label="Next image"
          >
            <IconChevronRight />
          </button>

          <div className="rc-dots">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`rc-dot ${i === index ? "is-active" : ""}`}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>

          <span className="rc-counter">
            <IconCamera />
            <span>{index + 1}/{total}</span>
          </span>
        </>
      )}
    </div>
  );
};

export default ManualImageSlider;